import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ITrainersData,
  ITrainersPage,
  ITrainersTool,
  ITrainerSubmit,
} from '@/types/trainers';
import {
  getTrainersContent,
  submitBenchmark,
  submitTrainersResponse,
} from '@/api/qa';
import { ApiErrorEnum } from '@/types/api';
import { useTrainerFeedback } from '@/hooks/useTrainerFeedback';

import Feedback from '@/components/ui/trainers/shared/Feedback';
import { getNormalizedImages, isValidOutcome } from '@/utils/trainers';
import { useTrainerReviews } from '@/hooks/useTrainerReviews';
import TrainerHeader from './TrainerHeader';
import TrainerFooter from './TrainerFooter';
import TrainerController, {
  TrainerButtons,
  TrainerInputs,
  TrainerSelect,
} from './TrainerController';
import TrainerButton from './TrainerButton';
import ErrorMessage from './shared/ErrorMessage';
import MergeTrainerContent from './content/MergeTrainerContent';

interface Props extends ITrainersPage {
  data?: ITrainersData;
  tool?: ITrainersTool;
  modifyPosition?: boolean;
  modifyAzimuth?: boolean;
}

const MergeTrainer: React.FC<Props> = ({
  data: initialData,
  page,
  name,
  description: initialDescription,
  location,
  tool,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const toolId = initialData?.toolId || '';
  const [data, setData] = useState<ITrainersData | undefined>(initialData);
  const { incrementReviews } = useTrainerReviews();
  const {
    leaseId,
    candidateFeatures = {},
    landmarkAssets,
  } = data?.content || {};

  const labels: { [key: string]: string } = {};
  Object.keys(candidateFeatures).forEach(key => {
    const label = candidateFeatures[key].label;
    labels[label] = label;
  });

  const titles = Object.keys(labels);
  const titleInfo: string =
    `*${titles.shift()?.replaceAll('-', ' ') || 'object'}*` || '';
  titles.forEach(title => {
    titleInfo.concat(' or ', title);
  });

  const description =
    initialDescription ||
    (data?.content
      ? `Are these images referring to the same exact ${titleInfo}`
      : '');

  const isDataReady = Boolean(data?.content);

  const [isBenchmark, setIsBenchmark] = useState(false);

  const { feedback, setFeedback } = useTrainerFeedback();

  const clearAllData = () => {
    setData(undefined);
    setIsBenchmark(false);
  };

  const refetchClient = useCallback(
    async (id: string) => {
      const _data: ITrainersData = await getTrainersContent(id, page);
      setData(_data);
    },
    [page],
  );

  const onBenchmarkToggle = (checked: boolean) => {
    setIsBenchmark(checked);
  };

  const onSubmit = async ({ outcome, value }: ITrainerSubmit) => {
    clearAllData();
    if (!leaseId) {
      return;
    }

    let match;

    if (!isValidOutcome(outcome)) {
      match = value;
    }
    const sending = {
      leaseId,
      ...(match && { match }),
      ...(outcome && { outcome }),
    };

    console.log('sending', sending);
    if (isBenchmark) {
      await submitBenchmark(toolId, sending);
    }

    const { error, benchmarkFeedback } = await submitTrainersResponse(
      toolId,
      sending,
    );

    if (benchmarkFeedback) {
      setFeedback({
        ...benchmarkFeedback,
        instructionsLink: tool?.args?.instructions,
      });
    }
    if (error) {
      setData({ error });
    } else {
      incrementReviews(toolId);
      refetchClient(toolId);
    }
  };

  const landmarkImages = getNormalizedImages(
    landmarkAssets?.frames,
    landmarkAssets?.closeFrames,
  );

  useEffect(() => {
    if (data && !data.error && !landmarkImages.length) {
      const error = ApiErrorEnum.NoContent;
      setData({ error });
    }
  }, [data, landmarkImages]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === '1') {
        onSubmit({ value: Object.keys(candidateFeatures)[0] });
      }
      if (event.key === '2') {
        onSubmit({ value: 'not a merge' });
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  });

  return (
    <div className="flex size-full flex-col">
      <TrainerHeader
        data={data}
        name={name}
        description={description}
        location={location}
        tool={tool}
        elementRef={headerRef}
      />
      {data?.error ? (
        <ErrorMessage error={data.error} />
      ) : (
        <>
          <MergeTrainerContent data={data} tool={tool} />
          <TrainerController
            containerRef={controllerRef}
            isDataReady={isDataReady}
            onSubmit={onSubmit}
            data={data}
            tool={tool}>
            <TrainerButton
              onClick={() =>
                onSubmit({ value: Object.keys(candidateFeatures)[0] })
              }
              disabled={!isDataReady}
              label="Yes"
            />
            <TrainerButton
              onClick={() => onSubmit({ value: 'not a merge' })}
              disabled={!isDataReady}
              label="No"
            />
            <TrainerButtons />
            <TrainerSelect />
            <TrainerInputs />
          </TrainerController>
        </>
      )}
      <TrainerFooter
        data={data}
        elementRef={footerRef}
        onBenchmarkToggle={onBenchmarkToggle}
        isBenchmark={isBenchmark}
      />
      {feedback?.feedbackMessage && <Feedback />}
    </div>
  );
};

export default MergeTrainer;
